import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type ItemListEntry = {
  id: string;
  sku: string;
  name: string;
  primaryBarcode: string | null;
  categoryId: string;
  categoryName: string;
  status: 'active' | 'archived';
  imageObjectKey: string | null;
};

export type ItemListResponse = {
  items: ItemListEntry[];
  page: number;
  pageSize: number;
  total: number;
};

export type ItemDetail = {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  categoryId: string;
  category: { id: string; name: string; slug: string };
  unitOfMeasure: 'each' | 'kg' | 'g' | 'l' | 'ml';
  taxCategory: string;
  ageRestriction: number;
  isVariableWeight: boolean;
  skipBaggingAllowed: boolean;
  isMarked: boolean;
  exciseStampCount: number;
  weightCheckMode: 'presence' | 'tolerance' | 'disabled';
  /** SSC-109: `operator` or `import` when chosen; null when only the default. */
  weightCheckModeSource: 'operator' | 'import' | null;
  weightTolerancePctOverride: number | null;
  /** SSC-108: the base unit's master-data weights, integer milligrams. */
  grossWeightMg: number | null;
  netWeightMg: number | null;
  /** `gp-compat` or `operator`; null when neither weight is known. */
  weightSource: string | null;
  imageObjectKey: string | null;
  status: 'active' | 'archived';
  barcodes: {
    id: string;
    barcode: string;
    isPrimary: boolean;
    attributes: unknown;
  }[];
  /** MICRO-43 P2a: `sourceSystem` is `gp-compat` for a row GP wrote. */
  attributes: { id: string; key: string; value: string; sourceSystem?: string | null }[];
};

export type ItemListQuery = {
  page?: number;
  pageSize?: number;
  categoryId?: string;
  status?: 'active' | 'archived';
  search?: string;
};

const LIST_KEY = (q: ItemListQuery) => ['catalog', 'items', q] as const;
const DETAIL_KEY = (id: string) => ['catalog', 'item', id] as const;

export function useItems(query: ItemListQuery = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: LIST_KEY(query),
    enabled: options.enabled ?? true,
    queryFn: async (): Promise<ItemListResponse> => {
      const res = await apiClient.get<ItemListResponse>('/catalog/items', {
        params: query,
      });
      return res.data;
    },
    placeholderData: (previous) => previous,
  });
}

/** Server-side ceiling on `pageSize` for `GET /catalog/items` (ListItemsQuerySchema). */
export const ITEM_LIST_MAX_PAGE_SIZE = 200;

/**
 * RL-367 — every active item in one category, paged to completion. Callers
 * that act on "the whole category" (the hotkey picker's "Select all") must
 * not stop at the first page: a single page is capped at
 * ITEM_LIST_MAX_PAGE_SIZE and anything past it would be dropped silently.
 */
export function useAllCategoryItems(categoryId: string) {
  return useQuery({
    queryKey: ['catalog', 'items', 'all-in-category', categoryId] as const,
    queryFn: async (): Promise<ItemListEntry[]> => {
      const all: ItemListEntry[] = [];
      for (let page = 1; ; page += 1) {
        const res = await apiClient.get<ItemListResponse>('/catalog/items', {
          params: { categoryId, page, pageSize: ITEM_LIST_MAX_PAGE_SIZE },
        });
        all.push(...res.data.items);
        // An empty page ends the walk even if `total` grew mid-read, so a
        // concurrent insert can never spin this loop.
        if (res.data.items.length === 0 || all.length >= res.data.total) return all;
      }
    },
  });
}

export function useItem(id: string | undefined) {
  return useQuery({
    queryKey: DETAIL_KEY(id ?? ''),
    enabled: id !== undefined && id !== '',
    queryFn: async (): Promise<ItemDetail> => {
      const res = await apiClient.get<ItemDetail>(`/catalog/items/${id}`);
      return res.data;
    },
  });
}

type CreateItemPayload = {
  sku: string;
  name: string;
  description?: string;
  categoryId: string;
  unitOfMeasure?: 'each' | 'kg' | 'g' | 'l' | 'ml';
  taxCategory: string;
  ageRestriction?: number;
  isVariableWeight?: boolean;
  skipBaggingAllowed?: boolean;
  isMarked?: boolean;
  exciseStampCount?: number;
  weightCheckMode?: 'presence' | 'tolerance' | 'disabled';
  weightTolerancePctOverride?: number | null;
  grossWeightMg?: number | null;
  netWeightMg?: number | null;
  status?: 'active' | 'archived';
  barcodes?: { barcode: string; isPrimary: boolean }[];
  attributes?: { key: string; value: string }[];
};

export function useCreateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateItemPayload): Promise<ItemDetail> => {
      const res = await apiClient.post<ItemDetail>('/catalog/items', payload);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalog', 'items'] }),
  });
}

export function useUpdateItem(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<CreateItemPayload>): Promise<ItemDetail> => {
      const res = await apiClient.patch<ItemDetail>(`/catalog/items/${id}`, patch);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DETAIL_KEY(id) });
      void qc.invalidateQueries({ queryKey: ['catalog', 'items'] });
    },
  });
}

export function useArchiveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/catalog/items/${id}`);
    },
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: DETAIL_KEY(id) });
      void qc.invalidateQueries({ queryKey: ['catalog', 'items'] });
    },
  });
}
